import { Button, Chip, SwipeableDrawer, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { EmptyList, TixlyIconButton } from '@tix-ticketing/ui-components';
import { ArrowRightIcon, CartIcon, CrossIcon } from '@tix-ticketing/ui-icons/outline';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';
import { CartOrder } from 'components/cart/cart-order';
import { ExchangeReturnedTickets } from 'components/common/exchange/exchange-returned-tickets';
import { useExchangeCancelPrompt } from 'components/common/exchange/use-exchange-cancel-prompt';
import { useAppState } from 'lib/app-state';
import { getOrderItemCount } from 'lib/helpers/order-helpers';
import { useExchangeCart } from 'lib/hooks/use-exchange-cart';
import { settleProductChanges, useProductChangesBusy } from 'lib/hooks/use-product-changes';
import { useReservationRemovals } from 'lib/hooks/use-reservation-removals';
import { CartOrderSummary } from './cart-order-summary';
interface MiniCartProps {
  isCartOpen: boolean;
  setIsCartOpen: (isOpen: boolean) => void;
}
export const MiniCart = ({
  isCartOpen,
  setIsCartOpen
}: MiniCartProps) => {
  const router = useRouter();
  const {
    eventData,
    isRenewalOrder
  } = useAppState();
  const {
    order
  } = eventData;

  // While a ticket exchange is in progress the order shown here is its staging
  // order, whose fees and amount due are recalculated after every edit.
  const exchange = useExchangeCart();
  const {
    promptCancelExchange,
    exchangeCancelDialog
  } = useExchangeCancelPrompt();
  // Removing an item from a reservation is a soft delete here too, so the
  // drawer's own checkout has to ask the same question the cart page does.
  const removals = useReservationRemovals();
  const orderItemCount = getOrderItemCount(removals.keptOrder);
  // A ticket change holds its own staging order and its returned tickets, so it
  // has something to show even before a replacement has been picked.
  const isEmpty = !exchange.isExchange && orderItemCount === 0;
  const isChangingProducts = useProductChangesBusy();
  return <>
      <SwipeableDrawer anchor="right" open={isCartOpen} onClose={() => setIsCartOpen(false)} onOpen={() => setIsCartOpen(true)} sx={{
      '.MuiPaper-root.MuiDrawer-paper': {
        width: 'calc(100% - 3rem)',
        maxWidth: '400px',
        height: 'calc(100% - (2 * 3 * 0.5rem))',
        m: 3,
        // The theme's Paper override gives the drawer its layer radius. The
        // footer is tinted edge-to-edge, so it has to be clipped to those
        // rounded corners.
        overflow: 'hidden'
      }
    }}>
        <Box sx={{
        p: 3,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2
      }}>
          <Typography component="div" sx={{
          fontSize: 28,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
            <FormattedMessage id="cart.title" defaultMessage={[{
            "type": 0,
            "value": "Your cart"
          }]} />
            <Chip label={orderItemCount} size="small" color={'primary'} />
          </Typography>
          <TixlyIconButton onClick={() => setIsCartOpen(false)} size="small" color="monochrome">
            <CrossIcon />
          </TixlyIconButton>
        </Box>
        {isEmpty ? <Box sx={{
        px: 3,
        pb: 3,
        flex: 1,
        display: 'grid',
        placeItems: 'center'
      }}>
            <EmptyList compact icon={<CartIcon />} title={<FormattedMessage id="cart.empty.title" defaultMessage={[{
          "type": 0,
          "value": "Your cart is empty"
        }]} />} description={<FormattedMessage id="cart.empty.description" defaultMessage={[{
          "type": 0,
          "value": "You don’t have any tickets or items in your cart"
        }]} />} action={<Button variant="contained" color="monochrome" size="large" onClick={() => {
          setIsCartOpen(false);
          router.push(/* TODO whitelabel based */'/');
        }}>
                  <FormattedMessage id="common.browseEvents" defaultMessage={[{
            "type": 0,
            "value": "Browse events"
          }]} />
                </Button>} />
          </Box> : <>
            <Box sx={{
          px: 3,
          pb: 3,
          flex: 1,
          overflowY: 'auto'
        }}>
              <CartOrder inAside onOrderChange={exchange.isExchange ? exchange.applyOrder : undefined}
          // The same gate the cart page applies, so a venue that has
          // turned off taking single items off an order is not worked
          // around from here: on a renewal `canRemoveItems` decides on
          // its own, while on a reservation it only guards the delete
          // endpoints a soft removal never reaches.
          allowRemoveTickets={isRenewalOrder ? order?.canRemoveItems !== false : removals.isActive || order?.canRemoveItems !== false} removals={removals}
          // An exchange hands back a fixed number of tickets and must take
          // exactly as many, so removing any of them cancels it — which is
          // confirmed before anything is removed.
          onRemoveIntercepted={exchange.isExchange ? () => {
            setIsCartOpen(false);
            promptCancelExchange();
          } : undefined} />
              {exchange.isExchange && exchange.returnedTickets.length > 0 && <ExchangeReturnedTickets inAside tickets={exchange.returnedTickets} />}
            </Box>
            <Stack spacing={1.5} sx={theme => ({
          p: 3,
          background: theme.palette.containerBackground.main
        })}>
              <CartOrderSummary hideItemsBreakdown plain exchange={exchange} removals={removals} />
              <Box sx={{
            display: 'flex',
            gap: 1,
            pt: 1
          }}>
                <Button variant="outlined" color="monochrome" fullWidth size="large" endIcon={<ArrowRightIcon />} disabled={removals.isActive && !removals.hasKeptItems} loading={removals.isCommitting || isChangingProducts} onClick={async () => {
              await settleProductChanges();
              setIsCartOpen(false);
              removals.proceedToCheckout(() => router.push('/checkout'));
            }}>
                  <FormattedMessage id="common.checkout" defaultMessage={[{
                "type": 0,
                "value": "Checkout"
              }]} />
                </Button>
                <Button variant="contained" fullWidth size="large" endIcon={<CartIcon />} onClick={() => {
              setIsCartOpen(false);
              router.push('/cart');
            }}>
                  <FormattedMessage id="cart.viewCart" defaultMessage={[{
                "type": 0,
                "value": "View cart"
              }]} />
                </Button>
              </Box>
            </Stack>
          </>}
      </SwipeableDrawer>
      {removals.releaseItemsDialog}
      {/* Outside the drawer: cancelling a change closes it. */}
      {exchangeCancelDialog}
    </>;
};